import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LabBrand from "../components/LabBrand";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email, password);
      await refresh();
      nav(from, { replace: true });
    } catch {
      setError("Invalid email or password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#f7f8f5] px-5 py-10">
      <div className="mb-10"><LabBrand /></div>
      <form
        onSubmit={onSubmit}
        className="card w-full max-w-md space-y-6 p-8 sm:p-10"
      >
        <div>
          <p className="eyebrow mb-3 text-brand-700">WELCOME TO THE LAB</p>
          <h1 className="font-serif text-3xl tracking-tight text-brand-900">A closer look starts here.</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Sign in to explore your anatomy collection and continue learning.
          </p>
        </div>

        <label className="block">
          <span className="label-text">Email</span>
          <div className="relative">
            <Mail
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="email"
              className="input pl-9"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
            />
          </div>
        </label>

        <label className="block">
          <span className="label-text">Password</span>
          <div className="relative">
            <Lock
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="password"
              className="input pl-9"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
        </label>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200">
            {error}
          </div>
        )}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"} {!busy && <ArrowRight size={15} />}
        </button>
      </form>

      <p className="mt-6 text-xs text-slate-400">Use the account provided by your teaching team.</p>
    </div>
  );
}
